import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { logActivity } from '../utils/activityLogger';
import '../styles/language-selector.css';

const LanguageSelector = ({ style = {} }) => {
  const { currentLanguage, changeLanguage, getAvailableLanguages, t, saveLanguageToProfile } = useLanguage();
  const { user } = useAuth();
  const availableLanguages = getAvailableLanguages();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get current language data
  const currentLang = availableLanguages.find(lang => lang.code === currentLanguage);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = async (newLanguage) => {
    const oldLanguage = currentLanguage;
    const newLangName = availableLanguages.find(lang => lang.code === newLanguage)?.name || newLanguage;
    
    logActivity('language_change', 'setting', null, 
      `User changed language from ${oldLanguage} to ${newLanguage}`,
      { 
        oldLanguage, 
        newLanguage, 
        newLanguageName: newLangName,
        userId: user?.id || null 
      });
    
    changeLanguage(newLanguage);
    setIsOpen(false);
    
    // Save to user profile if logged in
    if (user?.id) {
      await saveLanguageToProfile(newLanguage, user.id);
    }
  };

  // Get flag emoji for language
  const getFlagEmoji = (code) => {
    const flags = {
      'en': '🇺🇸',
      'fr': '🇫🇷', 
      'ru': '🇷🇺',
      'vi': '🇻🇳',
      'zh': '🇨🇳'
    };
    return flags[code] || '🌐';
  };

  return (
    <div className="language-selector-container" style={style}>
      <label className="language-selector-label">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.87,15.07L10.33,12.56L10.36,12.53C12.1,10.59 13.34,8.36 14.07,6H17V4H10V2H8V4H1V6H12.17C11.5,7.92 10.44,9.75 9,11.35C8.07,10.32 7.3,9.19 6.69,8H4.69C5.42,9.63 6.42,11.17 7.67,12.56L2.58,17.58L4,19L9,14L12.11,17.11L12.87,15.07Z"/>
        </svg>
        {t('language.select')}
      </label>
      
      <div className="language-dropdown" ref={dropdownRef}>
        <button 
          className={`language-dropdown-trigger ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          title={t('language.select')}
        >
          <div className="language-dropdown-current">
            <span className="language-flag">{getFlagEmoji(currentLanguage)}</span>
            <span className="language-name">{currentLang?.name}</span>
          </div>
          <svg 
            className={`dropdown-chevron ${isOpen ? 'open' : ''}`}
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="currentColor"
          >
            <path d="M7,10L12,15L17,10H7Z" />
          </svg>
        </button>

        {isOpen && (
          <div className="language-dropdown-menu">
            <div className="language-dropdown-content">
              {availableLanguages.map(lang => (
                <button
                  key={lang.code}
                  className={`language-option ${lang.code === currentLanguage ? 'selected' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <span className="language-flag">{getFlagEmoji(lang.code)}</span>
                  <span className="language-name">{lang.name}</span>
                  {lang.code === currentLanguage && (
                    <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;